import PropTypes from "prop-types";
import React, { Fragment, useRef, useState } from "react";
import { connect } from "react-redux";
import { getDiscountPrice } from "../../../helpers/product";
import { clearCart } from "../../../redux/actions/cartActions";
import { constructArtificialLinkNode, getStaticPageLink } from "../../../utils/links";
import Breadcrumb from "../../../components/magnolia/breadcrumb/Breadcrumb";
import HtmlHeader from "../../../components/magnolia/htmlHeader/HtmlHeader";
import LinkWrapper from "../../../components/magnolia/linkWrapper/LinkWrapper";
import CountrySelect from "../../../components/magnolia/countrySelect/CountrySelect";
import { decodeText } from "../../../utils/richText";

// Line total of a cart item in the current currency (discounted when a discount applies).
const lineTotal = (cartItem, currency) => {
    const discountedPrice = getDiscountPrice(cartItem.price, cartItem.discount);
    const unit = discountedPrice !== null ? discountedPrice : cartItem.price;
    return unit * currency.currencyRate * cartItem.quantity;
};

// Address lines from the billing form, empty fields left out.
const addressLines = (billing) =>
    [
        [billing.firstName, billing.lastName].filter(Boolean).join(" "),
        billing.company,
        billing.address1,
        billing.address2,
        [billing.zip, billing.city].filter(Boolean).join(" "),
        billing.state,
        billing.country,
    ].filter(Boolean);

const Address = ({ title, lines }) => (
    <div className="col-md-6 mb-4">
        <h3>{title}</h3>
        {lines.length ? (
            <p>
                {lines.map((line, idx) => (
                    <Fragment key={idx}>
                        {line}
                        <br />
                    </Fragment>
                ))}
            </p>
        ) : (
            <p className="text-muted">No address entered.</p>
        )}
    </div>
);

const OrderTable = ({ items, total, currency, currencySymbol }) => (
    <div className="your-order-wrap gray-bg-4">
        <div className="your-order-product-info">
            <div className="your-order-top">
                <ul>
                    <li>Product</li>
                    <li>Total</li>
                </ul>
            </div>
            <div className="your-order-middle">
                <ul>
                    {items.map((item, key) => (
                        <li key={key}>
                            <span className="order-middle-left">
                                {decodeText(item.displayName)} X {item.quantity}
                            </span>{" "}
                            <span className="order-price">{currencySymbol + lineTotal(item, currency).toFixed(2)}</span>
                        </li>
                    ))}
                </ul>
            </div>
            <div className="your-order-bottom">
                <ul>
                    <li className="your-order-shipping">Shipping</li>
                    <li>Free shipping</li>
                </ul>
            </div>
            <div className="your-order-total">
                <ul>
                    <li className="order-total">Total</li>
                    <li>{currencySymbol + total.toFixed(2)}</li>
                </ul>
            </div>
        </div>
    </div>
);

// Order confirmation after "Place Order", following the thank-you mockup on SERVDEMO-560:
// hero, four facts, order details with the addresses from the billing form, the order table.
const OrderConfirmation = ({ order, currency }) => {
    const lines = addressLines(order.billing);
    const homeLink = constructArtificialLinkNode({ value: getStaticPageLink("HOME") });

    return (
        <Fragment>
            <HtmlHeader title="Order confirmation" />
            <Breadcrumb />

            <div className="checkout-area order-confirmation pt-95 pb-100">
                <div className="container">
                    <div className="order-confirmation__hero">
                        <div className="order-confirmation__icon">
                            <i className="pe-7s-check"></i>
                        </div>
                        <h1>Thank you for your order!</h1>
                        <p>
                            Your order has been placed successfully. A confirmation has been sent to{" "}
                            {order.billing.email ? <strong>{order.billing.email}</strong> : "your email address"}. We will email you again as
                            soon as it ships.
                        </p>
                    </div>

                    <div className="order-confirmation__facts">
                        {[
                            ["Order Number", order.number],
                            ["Date", order.date],
                            ["Payment Method", "Credit Card"],
                            ["Total", order.currencySymbol + order.total.toFixed(2)],
                        ].map(([label, value]) => (
                            <div className="order-confirmation__fact" key={label}>
                                <span className="order-confirmation__fact-label">{label}</span>
                                <span className="order-confirmation__fact-value">{value}</span>
                            </div>
                        ))}
                    </div>

                    <div className="row">
                        <div className="col-lg-7">
                            <div className="order-confirmation__details">
                                <h2>Order details</h2>
                                <div className="row">
                                    <Address title="Shipping address" lines={lines} />
                                    <Address title="Billing address" lines={lines} />
                                </div>
                                <div className="order-confirmation__actions">
                                    <LinkWrapper linkNode={homeLink}>Continue shopping</LinkWrapper>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-5">
                            <div className="your-order-area">
                                <h3>Your order</h3>
                                <OrderTable items={order.items} total={order.total} currency={currency} currencySymbol={order.currencySymbol} />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </Fragment>
    );
};

const Checkout = ({ location, cartItems, currency, clearCart }) => {
    // Demo checkout: "Place Order" snapshots the cart and the billing form into a
    // confirmation and empties the cart; nothing is sent anywhere.
    const [order, setOrder] = useState(null);
    const billingForm = useRef(null);

    const placeOrder = () => {
        const form = billingForm.current;
        const field = (name) => (form && form.elements[name] ? form.elements[name].value.trim() : "");

        setOrder({
            number: `#OTD-${Math.floor(10000 + Math.random() * 90000)}`,
            date: new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" }),
            items: cartItems.map((cartItem) => ({ ...cartItem })),
            total: cartItems.reduce((sum, cartItem) => sum + lineTotal(cartItem, currency), 0),
            currencySymbol: currency.currencySymbol,
            billing: Object.fromEntries(
                ["firstName", "lastName", "company", "country", "address1", "address2", "city", "state", "zip", "phone", "email"].map((name) => [
                    name,
                    field(name),
                ]),
            ),
        });
        clearCart();
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (order) return <OrderConfirmation order={order} currency={currency} />;

    const cartTotalPrice = cartItems.reduce((sum, cartItem) => sum + lineTotal(cartItem, currency), 0);

    return (
        <Fragment>
            <HtmlHeader title="Checkout" />
            <Breadcrumb />

            <div className="checkout-area pt-95 pb-100">
                <div className="container">
                    {cartItems && cartItems.length >= 1 ? (
                        <div className="row">
                            <div className="col-lg-7">
                                <form className="billing-info-wrap" ref={billingForm} onSubmit={(e) => e.preventDefault()}>
                                    <h3>Billing Details</h3>
                                    <div className="row">
                                        <div className="col-lg-6 col-md-6">
                                            <div className="billing-info mb-20">
                                                <label>First Name</label>
                                                <input type="text" name="firstName" autoComplete="given-name" />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6">
                                            <div className="billing-info mb-20">
                                                <label>Last Name</label>
                                                <input type="text" name="lastName" autoComplete="family-name" />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="billing-info mb-20">
                                                <label>Company Name</label>
                                                <input type="text" name="company" autoComplete="organization" />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="billing-select mb-20">
                                                <label>Country</label>
                                                <CountrySelect name="country" />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="billing-info mb-20">
                                                <label>Street Address</label>
                                                <input
                                                    className="billing-address"
                                                    name="address1"
                                                    placeholder="House number and street name"
                                                    type="text"
                                                    autoComplete="address-line1"
                                                />
                                                <input name="address2" placeholder="Apartment, suite, unit etc." type="text" autoComplete="address-line2" />
                                            </div>
                                        </div>
                                        <div className="col-lg-12">
                                            <div className="billing-info mb-20">
                                                <label>Town / City</label>
                                                <input type="text" name="city" autoComplete="address-level2" />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6">
                                            <div className="billing-info mb-20">
                                                <label>State / County</label>
                                                <input type="text" name="state" autoComplete="address-level1" />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6">
                                            <div className="billing-info mb-20">
                                                <label>Postcode / ZIP</label>
                                                <input type="text" name="zip" autoComplete="postal-code" />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6">
                                            <div className="billing-info mb-20">
                                                <label>Phone</label>
                                                <input type="text" name="phone" autoComplete="tel" />
                                            </div>
                                        </div>
                                        <div className="col-lg-6 col-md-6">
                                            <div className="billing-info mb-20">
                                                <label>Email Address</label>
                                                <input type="email" name="email" autoComplete="email" />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="additional-info-wrap">
                                        <h4>Additional information</h4>
                                        <div className="additional-info">
                                            <label>Order notes</label>
                                            <textarea
                                                placeholder="Notes about your order, e.g. special notes for delivery. "
                                                name="notes"
                                                defaultValue={""}
                                            />
                                        </div>
                                    </div>
                                </form>
                            </div>

                            <div className="col-lg-5">
                                <div className="your-order-area">
                                    <h3>Your order</h3>
                                    <OrderTable items={cartItems} total={cartTotalPrice} currency={currency} currencySymbol={currency.currencySymbol} />
                                    <div className="place-order mt-25">
                                        <button type="button" className="btn-hover" onClick={placeOrder}>
                                            Place Order
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-lg-12">
                                <div className="item-empty-area text-center">
                                    <div className="item-empty-area__icon mb-30">
                                        <i className="pe-7s-cash"></i>
                                    </div>
                                    <div className="item-empty-area__text">
                                        No items found in cart to checkout <br />{" "}
                                        <LinkWrapper linkNode={constructArtificialLinkNode({ value: getStaticPageLink("HOME") })}>
                                            Shop Now
                                        </LinkWrapper>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

Checkout.propTypes = {
    cartItems: PropTypes.array,
    clearCart: PropTypes.func,
    currency: PropTypes.object,
    location: PropTypes.object,
};

const mapStateToProps = (state) => {
    return {
        cartItems: state.cartData,
        currency: state.currencyData,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        clearCart: () => {
            dispatch(clearCart());
        },
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(Checkout);